import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  BarChart,
  CurrencyValue,
  DisplaySwitch,
  Flex,
  LineChart,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

const displayOptions = [
  { key: 'balance', value: 'Balance' },
  { key: 'volume', value: 'Volume' }
];

export default function HistoricTabs({ walletAddress }) {
  const theme = useTheme();
  const [activeDisplayOption, setActiveDisplayOption] = useState('balance');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const [data, error] = useApi({
    url: `/stusds/depositors/${walletAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      switch (activeDisplayOption) {
        case 'volume':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('stUSDS'),
                iconSize: '0'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('stUSDS'),
                iconSize: '3'
              }}
            />
          );
      }
    };
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'volume':
        return [
          {
            xField: 'date',
            yField: 'deposit',
            label: 'Join',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'withdraw',
            label: 'Exit',
            backgroundColor: theme.colors.chartDown
          }
        ];
      case 'balance':
        return [
          {
            xField: 'date',
            yField: 'balance'
          }
        ];
    }
  }, [activeDisplayOption, theme.colors]);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        {activeDisplayOption === 'volume' ? (
          <BarChart
            key={activeDisplayOption}
            data={data.volume}
            datasets={datasets}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            subValueFormatter={subValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        ) : (
          <LineChart
            key={activeDisplayOption}
            data={data.balance}
            valueFormatter={valueFormatter}
            datasets={datasets}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        )}
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
